import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import {
  CollectionFilterProps,
  CollectionListConfig,
  DatabaseListConfig,
  DatabaseListType,
  ListConfig,
  ListMode,
  NoveltiesFilterProps,
  NoveltyCustomListConfig,
  ReleaseEntryFilterProps,
  ReleaseEntrySort,
  ReleaseEntrySorts,
  ReleaseFilterProps,
} from '@/utils'
import { TranslationKey } from '@/stores/localization.ts'
import {
  UserReleaseEntryStatusCollectionStatusType,
  UserReleaseEntryStatusCurrentReadStatusType,
  DatabaseListConfig as RawDatabaseListConfig,
  UserCustomList as RawUserCustomList,
  OrderDirection,
} from '../../__generated__/graphql.ts'
import dayjs from 'dayjs'
import {
  getDefaultCollectionFilter,
  getDefaultCollectionSorts,
  getDefaultEntrySorts,
  getDefaultNoveltyFilter,
  getDefaultReleaseEntryFilter,
  getDefaultVolumeSorts,
} from '@/utils/collection.ts'
import { listsLineBuilder } from '@/views/lists/config.ts'
import { databaseLineBuilder } from '@/views/database/components/config.ts'
import { noveltiesLineBuilder } from '@/views/overview/components/config.ts'
import {
  GET_PUBLISHED_DATABASE_LIST_CONFIGS,
  USER_CUSTOM_LIST_STATS,
} from '@/queries/customLists.ts'
import { sendError } from '@/utils/sendError.ts'
import { useUserCollectionStore, calcOwnedReleasedVolumes } from '@/stores/user-collection'
import { useUserListsStore } from '@/stores/user-lists'
import { listRowKey } from '@/stores/lists.crud.ts'
import { catalogQuery } from '@/data/catalogClient.ts'

function getReadDefaultSorts(): ReleaseEntrySorts {
  return getDefaultCollectionSorts().map((s) =>
    s.property === 'lastCollectionChanged'
      ? ({ ...s, property: 'lastReadChanged' } as ReleaseEntrySort)
      : s,
  )
}

const commonIgnoredFilterPropsForChangeDetection: CollectionListConfig['ignoredFilterPropsForChangeDetection'] =
  ['collectionStatus', 'readStatus']

function createCollectionConfig(
  id: string,
  labelKey: TranslationKey,
  collectionStatus: UserReleaseEntryStatusCollectionStatusType,
): CollectionListConfig {
  return {
    id,
    labelKey,
    category: 'collection',
    mode: 'filtered',
    statusFilter: 'collection',
    collectionStatus,
    readStatus: null,
    lineBuilder: listsLineBuilder,
    defaultFilter: getDefaultCollectionFilter({ statusFilter: 'collection', collectionStatus }),
    ignoredFilterPropsForChangeDetection: commonIgnoredFilterPropsForChangeDetection,
    defaultSort: getDefaultCollectionSorts(),
  }
}

function createReadConfig(
  id: string,
  labelKey: TranslationKey,
  readStatus: UserReleaseEntryStatusCurrentReadStatusType,
): CollectionListConfig {
  return {
    id,
    labelKey,
    category: 'read',
    mode: 'filtered',
    statusFilter: 'read',
    collectionStatus: null,
    readStatus,
    lineBuilder: listsLineBuilder,
    defaultFilter: getDefaultCollectionFilter({ statusFilter: 'read', readStatus }),
    ignoredFilterPropsForChangeDetection: commonIgnoredFilterPropsForChangeDetection,
    defaultSort: getReadDefaultSorts(),
  }
}

const collectionListConfigs: CollectionListConfig[] = [
  createCollectionConfig(
    'collection-collecting',
    'runningReleasesCollectingTabTitle',
    UserReleaseEntryStatusCollectionStatusType.Collecting,
  ),
  createCollectionConfig(
    'collection-done',
    'doneReleasesCollectingTabTitle',
    UserReleaseEntryStatusCollectionStatusType.Done,
  ),
  createCollectionConfig(
    'collection-planned',
    'planningReleasesTabTitle',
    UserReleaseEntryStatusCollectionStatusType.Planned,
  ),
  createCollectionConfig(
    'collection-paused',
    'pausedReleaseCollectingTabTitle',
    UserReleaseEntryStatusCollectionStatusType.Paused,
  ),
  createCollectionConfig(
    'collection-aborted',
    'abortedReleasesCollectingTabTitle',
    UserReleaseEntryStatusCollectionStatusType.Aborted,
  ),
  createCollectionConfig(
    'collection-removed',
    'removedReleasesCollectingTabTitle',
    UserReleaseEntryStatusCollectionStatusType.Sold,
  ),
  createReadConfig(
    'read-reading',
    'runningReleasesReadingTabTitle',
    UserReleaseEntryStatusCurrentReadStatusType.Reading,
  ),
  createReadConfig(
    'read-done',
    'doneReleasesReadingTabTitle',
    UserReleaseEntryStatusCurrentReadStatusType.Done,
  ),
  createReadConfig(
    'read-planned',
    'planningReleasesTabTitle',
    UserReleaseEntryStatusCurrentReadStatusType.Planned,
  ),
  createReadConfig(
    'read-paused',
    'pausedReleasesReadingTabTitle',
    UserReleaseEntryStatusCurrentReadStatusType.Paused,
  ),
  createReadConfig(
    'read-not-planned',
    'notPlannedReleasesTabTitle',
    UserReleaseEntryStatusCurrentReadStatusType.NotPlanned,
  ),
  createReadConfig(
    'read-aborted',
    'abortedReleasesReadingTabTitle',
    UserReleaseEntryStatusCurrentReadStatusType.Aborted,
  ),
]

function parseFilterObject<T extends object>(value: unknown): T {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as T
  }
  return {} as T
}

function parseSortArray(value: unknown): ReleaseEntrySorts {
  if (Array.isArray(value)) {
    return value as ReleaseEntrySorts
  }
  return []
}

function parseIgnoredProps(value: unknown): (keyof ReleaseFilterProps)[] {
  if (Array.isArray(value)) {
    return value as (keyof ReleaseFilterProps)[]
  }
  return []
}

function resolveDynamicFilterValues<T extends object>(filter: T): T {
  const resolved = { ...filter }
  for (const key of Object.keys(resolved) as (keyof T)[]) {
    const value = resolved[key]
    if (value === '$today') {
      ;(resolved as Record<string, unknown>)[key as string] = dayjs().format('YYYY-MM-DD')
    }
  }
  return resolved
}

function parseListMode(value: unknown): ListMode {
  return value === 'curated' ? 'curated' : 'filtered'
}

function parseListType(value: unknown): DatabaseListType {
  return value === 'licenses' ? 'licenses' : 'explore'
}

function toRuntimeDatabaseConfig(row: RawDatabaseListConfig): DatabaseListConfig {
  const parsedFilter = parseFilterObject<ReleaseEntryFilterProps>(row.filter)
  const parsedSort = parseSortArray(row.sort)
  const subtitleDe = (row.subtitleDe ?? '').trim()
  const subtitleEn = (row.subtitleEn ?? '').trim()
  const subtitle = subtitleDe || subtitleEn ? { de: subtitleDe, en: subtitleEn } : null
  return {
    id: row.id,
    urlName: row.urlName ?? row.id,
    labelKey: { de: row.nameDe ?? '', en: row.nameEn ?? '' },
    subtitle,
    category: 'database',
    mode: parseListMode(row.mode),
    listType: parseListType(row.listType),
    lineBuilder: databaseLineBuilder,
    defaultFilter: resolveDynamicFilterValues({
      ...getDefaultReleaseEntryFilter(),
      ...parsedFilter,
    }),
    ignoredFilterPropsForChangeDetection: parseIgnoredProps(
      row.ignoredFilterPropsForChangeDetection,
    ),
    defaultSort: parsedSort.length > 0 ? parsedSort : getDefaultEntrySorts(),
    hideSearch: row.hideSearch ?? true,
  }
}

function toRuntimeCollectionCustomConfig(row: RawUserCustomList): CollectionListConfig {
  const statusFilter: 'collection' | 'read' = row.statusFilter === 'read' ? 'read' : 'collection'
  const parsedFilter = parseFilterObject<CollectionFilterProps>(row.filter)
  const parsedSort = parseSortArray(row.sort)
  const mode = parseListMode(row.mode)
  // Custom release-entry lists always query the membership-based ReleaseEntry endpoint,
  // so they sort by ReleaseEntry fields (not collection-status fields).
  const defaultSort = getDefaultEntrySorts()
  const displayName = row.name ?? ''
  return {
    id: row.id,
    urlName: row.urlName || undefined,
    labelKey: { de: displayName, en: displayName },
    category: statusFilter,
    mode,
    statusFilter,
    collectionStatus: null,
    readStatus: null,
    lineBuilder: listsLineBuilder,
    defaultFilter: { ...getDefaultCollectionFilter({ statusFilter }), ...parsedFilter },
    ignoredFilterPropsForChangeDetection: commonIgnoredFilterPropsForChangeDetection,
    defaultSort: parsedSort.length > 0 ? parsedSort : defaultSort,
    isCustom: true,
    showTotalNumber: row.showTotalNumber ?? false,
    showTotalValue: row.showTotalValue ?? false,
  }
}

function toRuntimeNoveltyCustomConfig(row: RawUserCustomList): NoveltyCustomListConfig {
  const parsedFilter = parseFilterObject<NoveltiesFilterProps>(row.filter)
  const parsedSort = parseSortArray(row.sort)
  const displayName = row.name ?? ''
  return {
    id: row.id,
    urlName: row.urlName || undefined,
    labelKey: { de: displayName, en: displayName },
    category: 'novelty-custom',
    mode: 'curated',
    lineBuilder: noveltiesLineBuilder,
    defaultFilter: { ...getDefaultNoveltyFilter(), ...parsedFilter },
    ignoredFilterPropsForChangeDetection: [],
    defaultSort: parsedSort.length > 0 ? parsedSort : getDefaultVolumeSorts(),
    showTotalNumber: row.showTotalNumber ?? false,
    showTotalValue: row.showTotalValue ?? false,
  }
}

type UserCustomListConfig = CollectionListConfig | NoveltyCustomListConfig

function toRuntimeCustomConfig(row: RawUserCustomList): UserCustomListConfig {
  if (row.targetType === 'novelty') {
    return toRuntimeNoveltyCustomConfig(row)
  }
  return toRuntimeCollectionCustomConfig(row)
}

export type CustomListStats = {
  itemCount: number
  seriesCount: number
  volumeCount: number
  totalValue: number
}

export const useListsStore = defineStore('lists', () => {
  const databaseListConfigs = ref<DatabaseListConfig[]>([])
  const databaseListsLoaded = ref(false)
  const customListStats = ref<{ [listId: string]: CustomListStats }>({})

  const loadDatabaseLists = async () => {
    try {
      const result = await catalogQuery({
        query: GET_PUBLISHED_DATABASE_LIST_CONFIGS,
        variables: {
          where: { enabled: { equals: true } },
          orderBy: [{ order: OrderDirection.Asc }],
        },
        fetchPolicy: 'network-only',
      })
      const rows = result?.data?.databaseListConfigs ?? []
      databaseListConfigs.value = rows.map(toRuntimeDatabaseConfig)
    } catch (e) {
      sendError(e, { source: 'listsStore.loadDatabaseLists' })
      databaseListConfigs.value = []
    } finally {
      databaseListsLoaded.value = true
    }
  }

  const userLists = useUserListsStore()

  const customListConfigs = computed<UserCustomListConfig[]>(() =>
    userLists.optimisticCustomListRows().map((row) => {
      const config = toRuntimeCustomConfig(row)
      config.pending = userLists.isPendingCreate(listRowKey(row))
      return config
    }),
  )

  const loadCustomLists = () => userLists.loadCustomLists()

  const loadCustomListStats = async (listId: string) => {
    try {
      const result = await catalogQuery({
        query: USER_CUSTOM_LIST_STATS,
        variables: { listId },
        fetchPolicy: 'network-only',
      })
      const stats = result?.data?.userCustomListStats
      if (stats) {
        customListStats.value = { ...customListStats.value, [listId]: stats }
      }
    } catch (e) {
      sendError(e, { source: 'listsStore.loadCustomListStats' })
    }
  }

  const getCustomListStats = (listId: string): CustomListStats | undefined =>
    customListStats.value[listId]

  const refreshLoadedCustomListStats = useDebounceFn(
    () => Promise.all(Object.keys(customListStats.value).map(loadCustomListStats)),
    500,
  )

  watch(
    () => userLists.membershipRevision,
    () => void refreshLoadedCustomListStats(),
  )

  const getListsByStatusFilter = (statusFilter: 'collection' | 'read'): CollectionListConfig[] => {
    return collectionListConfigs.filter((c) => c.statusFilter === statusFilter)
  }

  const getDatabaseLists = (): DatabaseListConfig[] => {
    return databaseListConfigs.value
  }

  const getDatabaseListsByType = (listType: DatabaseListType): DatabaseListConfig[] => {
    return databaseListConfigs.value.filter((c) => c.listType === listType)
  }

  const getCustomLists = (): UserCustomListConfig[] => {
    return customListConfigs.value
  }

  const userCollection = useUserCollectionStore()

  const getListStats = (
    config: ListConfig,
  ): { series: number; volumes: number; released: number } | null => {
    const entries = Object.values(userCollection.collectionMap)
    if (config.category === 'collection') {
      if (!config.collectionStatus) return null
      const matching = entries.filter((c) => c.collectionStatus === config.collectionStatus)
      const owned = matching.reduce((acc, c) => acc + c.ownedVolumes, 0)
      const ownedReleased = matching.reduce((acc, c) => acc + calcOwnedReleasedVolumes(c), 0)
      const released = matching.reduce((acc, c) => acc + c.releasedVolumes, 0)
      const ownedEarly = owned - ownedReleased
      return {
        series: matching.length,
        volumes: owned,
        released: released + ownedEarly,
      }
    }
    if (config.category === 'read') {
      if (!config.readStatus) return null
      const matching = entries.filter((c) => c.currentReadStatus === config.readStatus)
      return {
        series: matching.length,
        volumes: matching.reduce((acc, c) => acc + c.readVolumes, 0),
        released: matching.reduce((acc, c) => acc + c.ownedVolumes, 0),
      }
    }
    return null
  }

  const getListById = (id: string): ListConfig | undefined => {
    return (
      collectionListConfigs.find((c) => c.id === id) ??
      databaseListConfigs.value.find((c) => c.urlName === id || c.id === id) ??
      customListConfigs.value.find((c) => c.urlName === id || c.id === id)
    )
  }

  return {
    databaseListConfigs,
    customListConfigs,
    databaseListsLoaded,
    customListsLoaded: userLists.customListsLoaded,
    customListsLoadFailed: userLists.customListsLoadFailed,
    loadDatabaseLists,
    loadCustomLists,
    getListsByStatusFilter,
    getDatabaseLists,
    getDatabaseListsByType,
    getCustomLists,
    getListById,
    getListStats,
    loadCustomListStats,
    getCustomListStats,
  }
})
